import { Component, Inject, OnDestroy, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AppNotificationService } from '@core/services/app-notification.service';
import { ConfirmDialogService } from '@shared/components/dialogs/confirm-dialog/confirm-dialog.service';
import { U2bValidators } from '@shared/validators/validators';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanyContact } from '@shared/models/company-contact';
import { Person } from '@shared/models/person';
import { CompanyApiService } from '@modules/bcm/@shared/services';
import { TranslationService } from '@core/translation/translation.service';

@Component({
    selector: 'person-company-relation',
    templateUrl: './person-company-relation.component.html',
    styleUrls: ['./person-company-relation.component.scss'],
    standalone: false
})
export class PersonCompanyRelationComponent implements OnDestroy {

    private _unsubscribeAll = new Subject<any>();

    formGroup: UntypedFormGroup;

    isSaving: boolean;

    person: Person;

    constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: any,
                private _dialogRef: MatDialogRef<PersonCompanyRelationComponent>,
                private _appNotificationService: AppNotificationService,
                private _confirmDialogService: ConfirmDialogService,
                private _companyApiService: CompanyApiService,
                private _formBuilder: UntypedFormBuilder) {
        this._dialogRef.addPanelClass('default-dialog');
        this._dialogRef.disableClose = true;

        this.person = data.person;

        this.formGroup = this._formBuilder.group({
            note: [null, [U2bValidators.maxLength(1024)]]
        });

    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    onClickClose(): void {

        if (this.formGroup.dirty) {
            return this._confirmDialogService
                .useWarnTheme()
                .setTitle(TranslationService.translate('common.confirm.attention'))
                .setBody(TranslationService.translate('common.unsavedChanges.leavePageBody'))
                .setYesButton({
                    text: TranslationService.translate('common.unsavedChanges.leavePageConfirmYes')
                })
                .setNoButton({
                    text: TranslationService.translate('common.cancel')
                })
                .openWithCallback(() => this._dialogRef.close());
        }

        this._dialogRef.close();
    }

    onClickSaveAndClose(): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this._appNotificationService.showError(TranslationService.translate('bcm.personCompanyRelation.error.noOrganisationSelected'));
        } else {

            const {
                companyForm,
                note
            } = this.formGroup.value;

            this._companyApiService.getOne(companyForm.company.id)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(company => {

                const companyContact: CompanyContact = {
                    personId: this.person.id,
                    companyId: companyForm.company.id,
                    company: company,
                    personName: this.person.fullName,
                    personMail: this.person.mail,
                    personPhone: this.person.phone,
                    note: note
                };

                // check if contact already exists
                const contactExists = this.person.companyContacts.findIndex(existingContacts => {
                    return existingContacts.companyId === companyContact.companyId;
                }) !== -1;

                if (contactExists) {
                    this._appNotificationService.showError(TranslationService.translate('bcm.personCompanyRelation.error.organisationAlreadyLinked'));
                    return;
                }

                this._dialogRef.close(companyContact);

            });

        }
    }
}
